import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Stack,
  IconButton,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link,
  CircularProgress,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useIntl } from "react-intl";
import SaveIcon from "@mui/icons-material/Save";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  fetchAction,
  fetchTask,
  fetchDepartment,
  fetchApplicationFunctions,
  fetchApplicationTask,
  fetchPartnerFunctionsByCategory,
  updateAction,
  fetchActionGuidelines,
  createGuideline,
  updateGuideline,
  deleteGuideline,
} from "../../api/companyApi";
import { useAuth } from "../../providers/AuthProvider";
import { useNotification } from "../../providers/NotificationProvider";
import BuildDepartmentDrawer from "../../components/build/BuildDepartmentDrawer";
import DraggablePaper from "../../components/common/DraggablePaper";

const NONE = "NONE";

// ── ガイドライン 追加/編集ダイアログ ─────────────────────────
function GuidelineFormDialog({ open, onClose, onSave, editData, intl }) {
  const t = (id) => intl.formatMessage({ id });
  const [guidelineName, setGuidelineName] = useState("");
  const [guidelineDescription, setGuidelineDescription] = useState("");
  const [guidelineUrl, setGuidelineUrl] = useState("");

  useEffect(() => {
    if (editData) {
      setGuidelineName(editData.guidelineName || "");
      setGuidelineDescription(editData.guidelineDescription || "");
      setGuidelineUrl(editData.url || "");
    } else {
      setGuidelineName("");
      setGuidelineDescription("");
      setGuidelineUrl("");
    }
  }, [editData, open]);

  const handleSave = () => {
    if (!guidelineName.trim()) return;
    onSave({
      ...(editData || {}),
      guidelineName: guidelineName.trim(),
      guidelineDescription: guidelineDescription.trim(),
      url: guidelineUrl.trim() || null,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperComponent={DraggablePaper}>
      <DialogTitle data-drag-handle sx={{ cursor: "move", userSelect: "none" }}>
        {editData ? t("action.guideline.edit.title") : t("action.guideline.add.title")}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label={t("action.guideline.name")}
            value={guidelineName}
            onChange={(e) => setGuidelineName(e.target.value)}
            size="small"
            fullWidth
            required
          />
          <TextField
            label={t("action.guideline.description")}
            value={guidelineDescription}
            onChange={(e) => setGuidelineDescription(e.target.value)}
            size="small"
            fullWidth
            multiline
            rows={4}
          />
          <TextField
            label={t("action.guideline.url")}
            value={guidelineUrl}
            onChange={(e) => setGuidelineUrl(e.target.value)}
            size="small"
            fullWidth
            placeholder="https://..."
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("common.cancel")}</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!guidelineName.trim()}
        >
          {t("common.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── メインコンポーネント ─────────────────────────────────────
export default function BuildDepartmentActionDetailPage() {
  const { businessDomainId, businessUnitId, departmentId, actionId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const { companyId } = useAuth();
  const t = (id) => intl.formatMessage({ id });

  // ── アクション基本情報 ──
  const [action, setAction] = useState(null);
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [appFunctions, setAppFunctions] = useState([]);
  const [partnerFunctions, setPartnerFunctions] = useState([]);
  const [selectedAppFunctionId, setSelectedAppFunctionId] = useState(NONE);
  const [selectedPartnerFunctionId, setSelectedPartnerFunctionId] = useState(NONE);
  const [saving, setSaving] = useState(false);

  // ── ガイドライン ──
  const [guidelines, setGuidelines] = useState([]);
  const [guidelinesLoading, setGuidelinesLoading] = useState(false);
  const [guidelineDialogOpen, setGuidelineDialogOpen] = useState(false);
  const [editingGuideline, setEditingGuideline] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  const listPath = `/build/${businessDomainId}/business-units/${businessUnitId}/departments/${departmentId}/actions`;

  // ── アクション + 関連データ読み込み ──
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const act = await fetchAction(actionId);
      setAction(act);

      // ベースとなるタスクを取得
      if (act.taskId) {
        try {
          const taskData = await fetchTask(act.taskId);
          setTask(taskData);
        } catch { /* タスク情報なし */ }
      }

      const dept = await fetchDepartment(departmentId);

      // アプリケーション機能（タスクIDが一致するものだけ）
      const filteredAppFns = [];
      for (const appCat of (dept.associatedApplicationCategories || [])) {
        let fns = [];
        try { fns = await fetchApplicationFunctions(appCat.applicationCategoryId); } catch { continue; }
        for (const fn of fns) {
          try {
            const appTask = await fetchApplicationTask(fn.applicationTaskId);
            if (appTask.taskId === act.taskId) filteredAppFns.push(fn);
          } catch { /* skip */ }
        }
      }
      setAppFunctions(filteredAppFns);
      if (act.applicationCategoryId) setSelectedAppFunctionId(String(act.applicationCategoryId));

      // パートナー機能（タスクIDが一致するものだけ）
      const filteredPartnerFns = [];
      for (const partnerCat of (dept.associatedPartnerCategories || [])) {
        let fns = [];
        try { fns = await fetchPartnerFunctionsByCategory(partnerCat.partnerCategoryId); } catch { continue; }
        for (const fn of fns) {
          if (fn.taskId === act.taskId) filteredPartnerFns.push(fn);
        }
      }
      setPartnerFunctions(filteredPartnerFns);
      if (act.partnerCategoryId) setSelectedPartnerFunctionId(String(act.partnerCategoryId));
    } catch (e) {
      notify.apiError(e);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionId, departmentId]);

  useEffect(() => { loadData(); }, [loadData]);

  // ── ガイドライン読み込み ──
  const loadGuidelines = useCallback(async () => {
    setGuidelinesLoading(true);
    try {
      const data = await fetchActionGuidelines(actionId);
      setGuidelines(Array.isArray(data) ? data : []);
    } catch {
      setGuidelines([]);
    } finally {
      setGuidelinesLoading(false);
    }
  }, [actionId]);

  useEffect(() => { loadGuidelines(); }, [loadGuidelines]);

  // ── アクション保存 ──
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...action };

      if (selectedAppFunctionId === NONE) {
        payload.applicationCategoryId = null;
        payload.applicationUrl = null;
      } else {
        const fn = appFunctions.find((f) => String(f.applicationFunctionId) === selectedAppFunctionId);
        if (fn) {
          payload.applicationCategoryId = fn.applicationFunctionId;
          payload.applicationUrl = fn.url;
        }
      }

      if (selectedPartnerFunctionId === NONE) {
        payload.partnerCategoryId = null;
        payload.personUrl = null;
      } else {
        const fn = partnerFunctions.find((f) => String(f.partnerFunctionId) === selectedPartnerFunctionId);
        if (fn) {
          payload.partnerCategoryId = fn.partnerFunctionId;
          payload.personUrl = fn.url;
        }
      }

      await updateAction(payload);
      notify.success(t("message.update.success"));
      navigate(listPath, { replace: true });
    } catch (err) {
      notify.apiError(err);
    } finally {
      setSaving(false);
    }
  };

  // ── ガイドライン 保存 ──
  const handleGuidelineSave = async (formData) => {
    try {
      if (editingGuideline) {
        await updateGuideline({
          ...formData,
          guidelineId: editingGuideline.guidelineId,
          companyId,
          targetId: actionId,
          guidelineTarget: "Action",
        });
        notify.success(t("action.guideline.updateSuccess"));
      } else {
        await createGuideline({
          ...formData,
          companyId,
          targetId: actionId,
          guidelineTarget: "Action",
        });
        notify.success(t("action.guideline.createSuccess"));
      }
      setGuidelineDialogOpen(false);
      setEditingGuideline(null);
      loadGuidelines();
    } catch (err) {
      notify.apiError(err);
    }
  };

  // ── ガイドライン 削除 ──
  const handleGuidelineDelete = async (guidelineId) => {
    try {
      await deleteGuideline(guidelineId);
      notify.success(t("action.guideline.deleteSuccess"));
      setDeleteConfirmId(null);
      setGuidelines((prev) => prev.filter((g) => g.guidelineId !== guidelineId));
    } catch (err) {
      notify.apiError(err);
    }
  };

  if (loading) {
    return <Box sx={{ p: 2 }}><Typography>{t("common.loading")}</Typography></Box>;
  }

  if (!action) {
    return (
      <Box>
        <Typography variant="h6" sx={{ mb: 2 }}>{t("build.department.action.detail.title")}</Typography>
        <Typography color="text.secondary" sx={{ mb: 2 }}>{t("common.noData")}</Typography>
        <Button variant="outlined" onClick={() => navigate(`/build/${businessDomainId}/business-units/${businessUnitId}/departments/${departmentId}/actions`)}>{t("common.back")}</Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* ── ヘッダー ── */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <BuildDepartmentDrawer />
        <Typography variant="h6">{t("build.department.action.detail.title")}</Typography>
      </Box>

      {/* ── ベースタスク表示 ── */}
      {task && (
        <Paper variant="outlined" sx={{ p: 2, mb: 3, maxWidth: 600, bgcolor: "grey.50" }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>
            {t("action.task")}
          </Typography>
          <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 1 }}>
            {task.jobName && (
              <Typography variant="body2" color="text.secondary">
                {task.jobName}
                {task.functionName && ` › ${task.functionName}`}
              </Typography>
            )}
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {task.taskName}
            </Typography>
            {task.taskDescription && (
              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-wrap" }}>
                {task.taskDescription}
              </Typography>
            )}
            {task.url && (
              <Button
                variant="text"
                size="small"
                startIcon={<OpenInNewIcon />}
                component={Link}
                href={task.url}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ alignSelf: "flex-start", px: 0 }}
              >
                {task.url}
              </Button>
            )}
          </Box>
        </Paper>
      )}

      {/* ── アクション基本情報フォーム ── */}
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{ display: "flex", flexDirection: "column", gap: 3, maxWidth: 600, mb: 4 }}
      >
        <TextField
          label={t("action.name")}
          value={action.actionName || ""}
          disabled
          fullWidth
          inputProps={{ style: { color: "rgb(0,0,0)" } }}
        />
        <TextField
          label={t("common.description")}
          value={action.actionDescription || ""}
          disabled
          multiline
          rows={3}
          fullWidth
          inputProps={{ style: { color: "rgb(0,0,0)" } }}
        />
        <FormControl fullWidth>
          <InputLabel>{t("action.applicationFunction")}</InputLabel>
          <Select
            value={selectedAppFunctionId}
            label={t("action.applicationFunction")}
            onChange={(e) => setSelectedAppFunctionId(e.target.value)}
          >
            <MenuItem value={NONE}><em>{t("common.none")}</em></MenuItem>
            {appFunctions.map((fn) => (
              <MenuItem key={fn.applicationFunctionId} value={String(fn.applicationFunctionId)}>
                {fn.applicationFunctionName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth>
          <InputLabel>{t("action.partnerFunction")}</InputLabel>
          <Select
            value={selectedPartnerFunctionId}
            label={t("action.partnerFunction")}
            onChange={(e) => setSelectedPartnerFunctionId(e.target.value)}
          >
            <MenuItem value={NONE}><em>{t("common.none")}</em></MenuItem>
            {partnerFunctions.map((fn) => (
              <MenuItem key={fn.partnerFunctionId} value={String(fn.partnerFunctionId)}>
                {fn.partnerFunctionName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Divider />
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button type="submit" variant="contained" startIcon={<SaveIcon />} disabled={saving}>
            {t("common.save")}
          </Button>
          <Button variant="outlined" onClick={() => navigate(`/build/${businessDomainId}/business-units/${businessUnitId}/departments/${departmentId}/actions`)}>
            {t("common.back")}
          </Button>
        </Box>
      </Box>

      {/* ── ガイドライン セクション ── */}
      <Paper variant="outlined" sx={{ p: 3, maxWidth: 700 }}>
        {/* ヘッダー */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <MenuBookIcon color="primary" />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {t("action.guideline.title")}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => { setEditingGuideline(null); setGuidelineDialogOpen(true); }}
          >
            {t("action.guideline.add.button")}
          </Button>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* ガイドライン一覧 */}
        {guidelinesLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : guidelines.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t("action.guideline.empty")}
          </Typography>
        ) : (
          <Stack spacing={1}>
            {guidelines.map((g, i) => (
              <Accordion
                key={g.guidelineId || i}
                disableGutters
                elevation={0}
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                  "&:before": { display: "none" },
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexGrow: 1, mr: 1 }}>
                    <MenuBookIcon fontSize="small" color="primary" />
                    <Typography variant="body2" sx={{ fontWeight: 600, flexGrow: 1 }}>
                      {g.guidelineName}
                    </Typography>
                    <Tooltip title={t("common.edit")}>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingGuideline(g);
                          setGuidelineDialogOpen(true);
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t("common.delete")}>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteConfirmId(g.guidelineId);
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  {g.guidelineDescription && (
                    <Typography
                      variant="body2"
                      sx={{ mb: 1.5, whiteSpace: "pre-wrap", lineHeight: 1.8, color: "text.secondary" }}
                    >
                      {g.guidelineDescription}
                    </Typography>
                  )}
                  {g.url && (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<OpenInNewIcon />}
                      component={Link}
                      href={g.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t("action.guideline.openUrl")}
                    </Button>
                  )}
                </AccordionDetails>
              </Accordion>
            ))}
          </Stack>
        )}
      </Paper>

      {/* ── ガイドライン 追加/編集ダイアログ ── */}
      <GuidelineFormDialog
        open={guidelineDialogOpen}
        onClose={() => { setGuidelineDialogOpen(false); setEditingGuideline(null); }}
        onSave={handleGuidelineSave}
        editData={editingGuideline}
        intl={intl}
      />

      {/* ── ガイドライン 削除確認ダイアログ ── */}
      <Dialog open={!!deleteConfirmId} onClose={() => setDeleteConfirmId(null)} PaperComponent={DraggablePaper}>
        <DialogTitle data-drag-handle sx={{ cursor: "move", userSelect: "none" }}>{t("action.guideline.deleteConfirm.title")}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">{t("action.guideline.deleteConfirm.message")}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmId(null)}>{t("common.cancel")}</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => handleGuidelineDelete(deleteConfirmId)}
          >
            {t("common.delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
